import React from 'react';
import { PivotTableData, ServiceAttributeSummaryData } from '../../models/PulseData';
import { SummaryType, NotifierType } from './PulseNav'; // Ensure NotifierType is defined/exported
import { FetchState } from '../../models/commonTypes';

import PivotView from '../pulse/PivotView';
import ServiceDetailView from '../pulse/ServiceDetailView';
import GroupAttributeView from '../pulse/GroupAttributeView';

// ID for the scrollable content area
const PULSE_CONTENT_BODY_ID = 'pulse-content-body-for-print';

export interface PivotViewRenderPayload {
  type: 'pivot';
  data: PivotTableData;
} // Export if used by PulsePage directly for typing payload

export interface ServiceDetailViewRenderPayload {
  type: 'serviceDetail';
  serviceName: string;
  keyAttributes: string[];
  serviceAttributesData: Record<string, ServiceAttributeSummaryData | null>;
  isLoadingAttributes: Record<string, boolean>;
  attributeErrors: Record<string, string | null>;
  selectedAttributeForServicePivot: string | null;
  serviceAttributePivotState: FetchState<PivotTableData>;
} // Export if used by PulsePage

export interface GroupAttributeViewRenderPayload {
  type: 'groupAttribute';
  servicesGroup: string;
  selectedGroupAttribute: string;
  groupAttributeSummaryState: FetchState<ServiceAttributeSummaryData>;
} // Export if used by PulsePage

interface NoViewRenderPayload {
  type: 'none';
} // Export if used by PulsePage

export type ViewRenderPayload =
  | PivotViewRenderPayload
  | ServiceDetailViewRenderPayload
  | GroupAttributeViewRenderPayload
  | NoViewRenderPayload;

interface PulseContentProps {
  activeSummary: SummaryType;
  servicesGroup: string;
  isLoadingOverall: boolean;
  currentStartDate: Date;
  currentEndDate: Date;
  viewRenderPayload: ViewRenderPayload;
  mainCssUrl: string;
  // showNotification can be passed if sub-views don't use the hook directly
  // showNotification: (message: string, type: NotifierType) => void;
}
  
const PulseContent: React.FC<PulseContentProps> = ({ 
    activeSummary, 
    servicesGroup,
    isLoadingOverall,
    currentStartDate, 
    currentEndDate,
    viewRenderPayload,
    mainCssUrl,
    // showNotification, // If passing down
  }) => {

  const commonViewProps = {
    servicesGroup,
    currentStartDate,
    currentEndDate,
    mainCssUrl,
    // showNotification, // If passing down
    isLoadingOverall,
    activeSummary,
  };

  const renderSummary = () => {
    switch (viewRenderPayload.type) {
      case 'pivot':
        return <PivotView {...viewRenderPayload} {...commonViewProps} />;
      case 'serviceDetail':
        // ServiceDetailViewProps matches the fields in ServiceDetailViewRenderPayload
        return <ServiceDetailView {...viewRenderPayload} {...commonViewProps} />;
      case 'groupAttribute':
        return <GroupAttributeView {...viewRenderPayload} {...commonViewProps} />;
      case 'none':
        return <p>Select a summary from the navigation.</p>;
      default:
        // const _exhaustiveCheck: never = viewRenderPayload; // For exhaustive type checking
        // This case should ideally not be reached if viewRenderPayload is typed correctly
        // const _exhaustiveCheck: never = viewRenderPayload;
        return <p>Invalid view configuration.</p>;
    }
  };

  return (
    <div className="pulse-content">
      <div id={PULSE_CONTENT_BODY_ID} className="pulse-content-body">
        {renderSummary()}
      </div>
    </div>
  );
};

export default PulseContent;